import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { type Block, type Post, tokenize } from './post-parser'
import { thaiRoute } from 'lib/dictionary-routes'
import { CHIP_TONES } from './chip-tones'
import NewsletterCta from './newsletter-cta'

/**
 * The layout for every post under /blog: a split hero over a single reading
 * column of hairline-ruled sections, closing with the newsletter
 * sign-up. It renders whatever post it's handed, so publishing another one is
 * a markdown file plus a line in `posts.ts` — see the notes there.
 *
 * The copy lives in `src/content/blog/<slug>.md` (parsed by `post-parser.ts`);
 * this component only supplies the layout. The page's title and description
 * come from the route's `head`, not from here.
 */

// A lightly obfuscated email link: it looks and clicks like a normal address,
// but the rendered DOM never contains a scrapable `user@domain` text node (the
// `@` is injected via CSS) nor a `mailto:` href until the user hovers/focuses
// the link. Keeps casual harvesters from lifting the address off the page.
const EmailLink = ({ address }: { address: string }): React.ReactElement => {
   const [local, domain] = address.split('@')
   const [href, setHref] = useState<string | undefined>(undefined)
   const reveal = () => setHref(`mailto:${address}`)
   return (
      <a
         href={href}
         className="text-lightBlue underline underline-offset-2 cursor-pointer hover:text-lightBlueDarkVariation"
         onMouseEnter={reveal}
         onFocus={reveal}
         onClick={(event) => {
            if (href === undefined) {
               event.preventDefault()
               window.location.href = `mailto:${address}`
            }
         }}
      >
         {local}
         <span className="before:content-['@']" aria-hidden="true" />
         {domain}
      </a>
   )
}

// Render one line of inline text: `translit`, headwords, *emphasis* and Thai runs.
const renderInline = (text: string): React.ReactNode =>
   tokenize(text).map((token, index) => {
      switch (token.kind) {
         case 'translit':
            return (
               <span key={index} className="font-translit text-translit italic">
                  {token.value}
               </span>
            )
         case 'headword':
            // A word the post is citing, so it goes to its own entry. Set in the
            // same Thai face and size as `.thai` below, but coloured and
            // underlined as the link it is.
            return (
               <Link
                  key={index}
                  {...thaiRoute(token.value)}
                  className="font-thai text-6 text-translit underline decoration-from-font underline-offset-4 hover:text-lightBlueDarkVariation"
               >
                  {token.value}
               </Link>
            )
         case 'em':
            return <em key={index}>{token.value}</em>
         case 'thai':
            return (
               <span key={index} className="thai">
                  {token.value}
               </span>
            )
         case 'email':
            return <EmailLink key={index} address={token.value} />
         case 'text':
            return <React.Fragment key={index}>{token.value}</React.Fragment>
      }
   })

const renderBlock = (block: Block, index: number): React.ReactNode => {
   switch (block.type) {
      case 'list':
         return (
            <ul
               key={index}
               className="m-0 pl-[22px] text-[17px] sm:text-[18px] leading-[1.7] text-blogGray list-disc [&_li]:list-disc [&_li]:my-[4px]"
            >
               {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderInline(item)}</li>
               ))}
            </ul>
         )
      case 'callout':
         return (
            <div
               key={index}
               className="flex gap-[14px] items-start bg-nutshellBg border border-coralBorder rounded-[16px] p-[20px_24px]"
            >
               {block.icon && (
                  <span className="material-icons flex-none mt-[1px] text-[22px] text-coral">
                     {block.icon}
                  </span>
               )}
               <p className="m-0 text-[17px] sm:text-[18px] leading-[1.65] text-bodyBlack text-pretty">
                  {renderInline(block.text)}
               </p>
            </div>
         )
      case 'chips':
         return (
            <div key={index} className="flex flex-wrap items-center gap-[10px]">
               {block.chips.map((chip, chipIndex) => (
                  <span
                     key={chipIndex}
                     className="inline-flex items-center px-[14px] py-[6px] rounded-full bg-chipBg border border-chipBorder font-translit italic text-[15.5px] text-translit"
                  >
                     {chip}
                  </span>
               ))}
               {block.note && <span className="text-[14px] text-textMuted">{block.note}</span>}
            </div>
         )
      case 'examples':
         return (
            <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
               {block.examples.map((example, exampleIndex) => (
                  <div
                     key={exampleIndex}
                     className="bg-exampleBubble border border-exampleBubbleBorder rounded-[16px_16px_16px_5px] p-[14px_16px]"
                  >
                     <div className="flex items-baseline gap-[9px]">
                        <span className="font-thai text-[20px] text-exampleWord">
                           {example.word}
                        </span>
                        <span className="text-[13.5px] font-bold tracking-[0.06em] uppercase text-textMuted">
                           {example.sense}
                        </span>
                     </div>
                     <div className="mt-[8px] font-thai text-[17px] leading-[1.5] text-thaiWord">
                        {example.thai}
                     </div>
                     <div className="mt-[2px] font-translit italic text-[14.5px] text-translit">
                        {example.translit}
                     </div>
                     <div className="mt-[4px] text-[15px] leading-[1.5] text-bodyGray">
                        {example.english}
                     </div>
                  </div>
               ))}
            </div>
         )
      case 'paragraph':
         return (
            <p
               key={index}
               className="m-0 text-[17px] sm:text-[18px] leading-[1.7] text-blogGray text-pretty"
            >
               {renderInline(block.text)}
            </p>
         )
   }
}

const renderBlocks = (blocks: Block[]): React.ReactNode => blocks.map(renderBlock)

const BlogPost = ({ post }: { post: Post }) => {
   const { meta, lede, features, outro } = post
   const byline = [meta.author, meta.date, meta.readTime].filter(Boolean)

   return (
      <main className="bg-white font-latin text-blogGray px-[20px]">
         {/* The site's measure: the same 900px the header's logo and search box
             sit on, so the hero lines up with the chrome above it. */}
         <div className="max-w-[900px] mx-auto">
            {/* Split hero — copy left, illustration right */}
            {/* No top padding: the sticky header above already sets the gap. */}
            <header className="grid md:grid-cols-[1.15fr_0.85fr] md:items-center gap-[24px] md:gap-[40px] pb-[32px] md:pb-[44px]">
               <div className="flex flex-col items-start gap-[16px]">
                  {meta.chips.length > 0 && (
                     <div className="flex flex-wrap gap-[8px]">
                        {meta.chips.map((chip, index) => (
                           <span
                              key={index}
                              className="inline-flex items-center px-[13px] py-[5px] rounded-full text-[11.5px] font-bold tracking-[0.09em] uppercase"
                              style={{
                                 background: CHIP_TONES[chip.tone].wash,
                                 color: CHIP_TONES[chip.tone].ink,
                              }}
                           >
                              {chip.label}
                           </span>
                        ))}
                     </div>
                  )}
                  <h1 className="m-0 text-[32px] md:text-[42px] font-extrabold leading-[1.08] tracking-[-0.018em] text-bodyBlack text-pretty">
                     {meta.title}
                  </h1>
                  <p className="m-0 text-[18px] md:text-[20px] leading-[1.5] text-bodyGray text-pretty">
                     {meta.subtitle}
                  </p>
                  <div className="flex items-center gap-[10px] text-[14px] text-textMuted">
                     {byline.map((part, index) => (
                        <React.Fragment key={index}>
                           {index > 0 && <span aria-hidden="true">·</span>}
                           <span>{part}</span>
                        </React.Fragment>
                     ))}
                  </div>
               </div>
               {meta.heroImage && (
                  // A tall illustration would otherwise set the height of the
                  // whole hero and leave the copy stranded in white space.
                  <img
                     className="block w-full max-w-[420px] max-h-[360px] object-contain mx-auto md:mx-0 md:justify-self-end"
                     src={meta.heroImage}
                     alt={meta.heroAlt}
                  />
               )}
            </header>

            {/* Editorial body — one reading measure, sections ruled off by hairlines */}
            <div className="max-w-[720px] mx-auto border-t border-hairline pt-[32px] md:pt-[44px]">
               {/* The lede sets slightly larger than the body copy that follows */}
               <div className="flex flex-col gap-[16px] [&>p]:text-[19px] [&>p]:leading-[1.65] [&>p]:text-bodyBlack md:[&>p]:text-[21px]">
                  {renderBlocks(lede)}
               </div>

               {features.map((feature, index) => (
                  <section
                     key={index}
                     className="mt-[36px] md:mt-[44px] pt-[28px] md:pt-[36px] border-t border-hairline"
                  >
                     <h2 className="m-0 text-[23px] md:text-[27px] font-extrabold leading-[1.25] tracking-[-0.01em] text-bodyBlack text-pretty">
                        {feature.title}
                     </h2>
                     <div className="mt-[18px] flex flex-col gap-[16px]">
                        {renderBlocks(feature.blocks)}
                     </div>
                  </section>
               ))}

               {outro.length > 0 && (
                  <div className="mt-[36px] md:mt-[44px] pt-[28px] md:pt-[36px] border-t border-hairline flex flex-col gap-[20px]">
                     {renderBlocks(outro)}
                  </div>
               )}

               <NewsletterCta copy={meta} />
            </div>
         </div>
      </main>
   )
}

export default BlogPost
